import { useEffect, useState, type RefObject } from 'react';
import { useDebouncedValue, useResizeObserver } from 'app/lib/hooks';

const RESIZE_DEBOUNCE_DELAY_MS = 500;

const itemPathForId = (id: string): string => `dimensions-${id}`;

interface Dimension {
    width?: string | number;
    height?: string | number;
}

function initializer(
    id: string | undefined,
    enabled: boolean,
    defaultSize: Dimension
): Dimension | (() => Dimension) {
    if (!id || !enabled) {
        return defaultSize;
    }

    return (): Dimension => {
        const stored = localStorage.getItem(itemPathForId(id));
        return stored ? (JSON.parse(stored) as Dimension) : defaultSize;
    };
}

export function useStoredDialogSize(
    id: string | undefined,
    ref: RefObject<HTMLElement | null>,
    defaultSize: Dimension,
    enabled: boolean = false
): Dimension {
    const [size] = useState<Dimension>(initializer(id, enabled, defaultSize));

    const observed = useResizeObserver(ref);
    const debouncedWidth = useDebouncedValue(observed.width, RESIZE_DEBOUNCE_DELAY_MS);
    const debouncedHeight = useDebouncedValue(observed.height, RESIZE_DEBOUNCE_DELAY_MS);

    useEffect(() => {
        if (!id) {
            return;
        }

        if (!enabled) {
            localStorage.removeItem(itemPathForId(id));
            return;
        }

        if (debouncedWidth === 0 || debouncedHeight === 0) {
            return;
        }

        if (debouncedWidth === size.width && debouncedHeight === size.height) {
            return;
        }

        localStorage.setItem(
            itemPathForId(id),
            JSON.stringify({
                width: debouncedWidth,
                height: debouncedHeight
            })
        );
    }, [id, enabled, size, debouncedWidth, debouncedHeight]);

    return size;
}
